// A singleton class to track popovers and their stacking order.
// This class automatically monitors all elements with the `popover` attribute and watches
// for toggle events to add or remove them from the stack. The mere presence of the `popover` attribute
// guarantees that the element is treated as a popover, and it will be added to the stack when opened.
//
// This class contains only the following public methods:
// - `get popovers`: returns an array of all popovers in the stack.
// - `get topPopover`: returns the top popover in the stack.
// - `isTopPopover(node)`: returns true if the node is the top popover in the stack.
// - `isChildOfTopPopover(node)`: returns true if the node is a child of the top popover in the stack.
// - `topPopoverContains(node)`: returns true if the node either is or is within the top popover in the stack.
// - `moveToTop(node)`: moves the node to the top of the stack by quickly hiding and showing it.
//

class PopoverStacker {
  readonly #elements: Set<HTMLElement> = new Set();

  readonly #togglePopover = (event: ToggleEvent) => {
    const { currentTarget, newState } = event;
    if (currentTarget instanceof HTMLElement) {
      if (newState === 'open') {
        this.addPopover(currentTarget);
      } else if (newState === 'closed') {
        this.removePopover(currentTarget);
      }
    }
  };

  constructor() {
    document.addEventListener('DOMContentLoaded', () => {
      iripo.in('[popover]', node => {
        // If the node is already open when the popover attribute is added (ex. both happen at the same time)
        // then we need to add it to the popover stack.
        if (node.matches(':popover-open')) this.addPopover(node);

        // NOTE: The `toggle` event does not bubble, so we have to listen for the event on the node itself.
        node.addEventListener('toggle', this.#togglePopover);
      });

      iripo.out('[popover]', node => {
        this.removePopover(node);
        node.removeEventListener('toggle', this.#togglePopover);
      });
    });
  }

  private addPopover(node: HTMLElement) {
    // If the node is already in the stack, we need to remove it first.
    if (this.#elements.has(node)) this.#elements.delete(node);

    this.#elements.add(node);
  }

  private removePopover(node: HTMLElement) {
    this.#elements.delete(node);
  }

  get popovers(): HTMLElement[] {
    return Array.from(this.#elements);
  }

  get topPopover(): HTMLElement | undefined {
    if (!this.popovers.length) return;
    const popovers = this.popovers.slice().reverse();

    // Return the first popover that is currently open. We add a guard to check that
    // popover is connected to the DOM to avoid issues with detached nodes and also
    // to ensure that the popover is also popped open.
    return popovers.find(popover => {
      // If the popover is not connected to the DOM, we remove it from the stack.
      // This ensures that we don't leak memory by keeping references to detached nodes.
      if (!popover.isConnected) {
        this.removePopover(popover);
        return false;
      }

      return popover.matches(':popover-open');
    });
  }

  // Whether the node is the top popover in the stack.
  isTopPopover(node: HTMLElement): boolean {
    return this.topPopover === node;
  }

  // Whether the node is a child of the top popover in the stack.
  isChildOfTopPopover(node: HTMLElement): boolean {
    return this.topPopover?.contains(node) ?? false;
  }

  // Whether the node either IS or IS CONTAINED BY the top popover in the stack.
  topPopoverContains(node: HTMLElement): boolean {
    // If the node is the top popover, it is considered within itself.
    if (this.isTopPopover(node)) return true;

    return this.topPopover?.contains(node) ?? false;
  }

  moveToTop(node: HTMLElement) {
    if (!node.hasAttribute('popover')) node.setAttribute('popover', 'manual');

    // A popover itself may contain other open popovers, so we need to find all nested popovers
    // and ensure they are also moved to the top of the stack. Because the popovers array
    // is ordered from bottom to top, we can simply find all popovers that contain the node
    // and then show them one by one, which will ensure they are at the top of the stack.
    const popoversToMoveToTop = this.popovers.reduce((acc, popover) => {
      if (node.contains(popover)) acc.add(popover);
      return acc;
    }, new Set([node]));

    popoversToMoveToTop.forEach(popover => {
      // If the popover is not connected to the DOM, we remove it from the stack.
      if (!popover.isConnected) {
        this.removePopover(popover);
        return;
      }

      // Quickly hide and show the popover to ensure it is at the top of the visual stack.
      // Because the popover toggle events will fire in response to the hide/show, the element will
      // be automatically added to the stack at the top.
      if (popover.matches(':popover-open')) popover.hidePopover();
      popover.showPopover();
    });
  }
}

const popoverStacker = new PopoverStacker();
export default popoverStacker;
